import React from 'react';
import { useAuth } from '../../context/AuthContext';
import { Navigate, useParams } from 'react-router-dom';
import { hasAnyRole } from '../../utils/roleUtils';
import { hasModuleAccess, hasElementaryItemAccess } from '../../utils/subscriptionUtils';
import type { ModuleId } from '../../types';
import type { ElementaryItemId } from '../../services/api_subscriptions';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRoles?: string[]; // Array of roles that can access this route
  fallbackPath?: string; // Where to redirect if access denied
  requiredModule?: ModuleId;
  requiredElementaryItem?: ElementaryItemId;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRoles = [],
  fallbackPath = '/projects',
  requiredModule,
  requiredElementaryItem,
}) => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const { locale } = useParams<{ locale: string }>();

  // Show loading while checking authentication
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect to root if not authenticated (App.tsx will handle showing LoginForm)
  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  // If no specific roles required, allow access to authenticated users
  if (requiredRoles.length === 0 && !requiredModule && !requiredElementaryItem) {
    return <>{children}</>;
  }

  // Check if user has any of the required roles
  if (requiredRoles.length > 0 && !hasAnyRole(user, requiredRoles)) {
    return <Navigate to={fallbackPath} replace />;
  }

  // Subscription-based module gating
  if (requiredModule && !hasModuleAccess(user, requiredModule)) {
    return <Navigate to={`/${locale || 'en'}/dashboard`} replace />;
  }

  // Subscription-based elementary item gating
  if (requiredElementaryItem && !hasElementaryItemAccess(user, requiredElementaryItem)) {
    return <Navigate to={`/${locale || 'en'}/dashboard`} replace />;
  }

  return <>{children}</>;
};